import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  Input,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { take } from 'rxjs';
import { QuestionButtonComponent } from '../../../basic/buttons/question-button/question-button.component';
import { GoalService } from '../../../core/goal/goal.service';
import { MethodologyService } from '../../../core/methodology/methodology.service';
import { ObjectionService } from '../../../core/objection/objection.service';
import { ProductService } from '../../../core/product/product.service';
import { Resume } from '../../../core/resume/resume';
import { ResumeService } from '../../../core/resume/resume.service';
import { SessionTemplate, SessionTemplateUnSafeSubData } from '../../../core/session-template/session-template';
import { SessionTemplateService } from '../../../core/session-template/session.template.service';
import { SessionCreativity, SessionPerformance } from '../../../core/session/session';
import { sessionLanguages } from '../../../core/session/session-language';
import { SessionService } from '../../../core/session/session.service';
import { WorkspaceHolderService } from '../../../core/workspace/workspace.holder.service';
import { EnumUtils } from '../../../shared/utils/enum-utils';
import { ProfileCardSmallComponent } from '../../profile-card/profile-card-small/profile-card-small.component';
import { EditObjectionListSessionTemplateComponent } from '../edit-objection-list-session-template/edit-objection-list-session-template.component';

@Component({
  selector: 'app-new-edit-session-template',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzRadioModule,
    NzSelectModule,
    NzSegmentedModule,
    NzPopconfirmModule,
    NzInputNumberModule,

    QuestionButtonComponent,
    ProfileCardSmallComponent,
    EditObjectionListSessionTemplateComponent,
  ],
  templateUrl: './new-edit-session-template.component.html',
  styleUrl: './new-edit-session-template.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewEditSessionTemplateComponent {
  @Input() sessionTemplate?: SessionTemplate;

  @Input() isEdit: boolean = false;

  @Input() isVisible = false;
  @Output() isVisibleChange = new EventEmitter<boolean>();

  isEmotionalObjection = signal<boolean>(false);

  isEditObjectionListVisible = false;

  languageList = sessionLanguages;

  goalList = toSignal(this.goalService.goalList$(), { initialValue: [] });
  methodologyList = toSignal(this.methodologyService.methodologyList$(), { initialValue: [] });
  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: [] });
  productList = toSignal(this.productService.productList$(), { initialValue: [] });

  getResume = (id?: string) =>
    computed(() => {
      const resumeList = this.resumeList();
      return resumeList.find((resume) => resume.id === id);
    });

  getProduct = (id?: string) =>
    computed(() => {
      const productList = this.productList();
      return productList.find((product) => product.id === id);
    });

  getGoal = (id?: string) =>
    computed(() => {
      const goalList = this.goalList();
      return goalList.find((goal) => goal.id === id);
    });

  getMethodology = (id?: string) =>
    computed(() => {
      const methodologyList = this.methodologyList();
      return methodologyList.find((methodology) => methodology.id === id);
    });

  validateForm: FormGroup<{
    title: FormControl<string>;

    instruction: FormControl<string>;

    targetCallDuration: FormControl<number>;

    salesCycle: FormControl<string>;

    language: FormControl<string>;

    goal: FormControl<string>;
    methodology: FormControl<string>;
    resume: FormControl<string>;
    product: FormControl<string>;

    creativity: FormControl<number>;
    performance: FormControl<number>;

    additionalRubrics: FormControl<string>;

    [key: string]: FormControl<boolean | string | number>;
  }>;

  instructionPlaceholder = `Please be as descriptive as possible on what you want the AI customer to do. For example: “Ask the seller these 5 specific objections” or “Tell the seller [XYZ] reasons why you’re an unhappy customer” `;

  creativityOptions = EnumUtils.getAllValues(SessionCreativity);
  performanceOptions = EnumUtils.getAllValues(SessionPerformance);

  creativityExplain = `
  **Deterministic:** Produces precise and consistent answers.
  
  **Creative:** Generates creative and varied answers.`;

  objectionList = toSignal(this.objectionService.itemList$(), { initialValue: [] });

  getObjection = (id?: string) => computed(() => this.objectionList().find((item) => item.id === id));

  constructor(
    private objectionService: ObjectionService,
    private sessionTemplateService: SessionTemplateService,
    private goalService: GoalService,
    private methodologyService: MethodologyService,
    private resumeService: ResumeService,
    private productService: ProductService,
    private workspaceHolderService: WorkspaceHolderService,
    private sessionService: SessionService,
    private fb: NonNullableFormBuilder
  ) {
    this.validateForm = this.fb.group({
      title: ['', [Validators.required]],

      instruction: ['', []],

      targetCallDuration: [30, [Validators.required]],

      salesCycle: ['', [Validators.required]],

      language: ['en', [Validators.required]],

      goal: ['', [Validators.required]],
      methodology: ['', [Validators.required]],
      resume: ['', [Validators.required]],
      product: ['', [Validators.required]],

      creativity: [0, [Validators.required]],
      performance: [0, [Validators.required]],

      additionalRubrics: [''],
    });
  }

  salesCycleList = computed(() => {
    const currentWorkspace = this.workspaceHolderService.currentWorkspace();

    return currentWorkspace.settings?.salesCycleList;
  });

  getResumeText(resume?: Resume) {
    if (!resume) {
      return '';
    }
    return `${resume.subData.content?.firstName} ${resume.subData.content?.lastName} - ${resume.subData.content?.mostRecentPosition} @
    ${resume.subData.content?.mostRecentCompany}`;
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (propName === 'sessionTemplate') {
        const chng = changes[propName];

        if (chng.previousValue !== chng.currentValue) {
          this.validateForm.patchValue({
            title: chng.currentValue?.title ?? '',
            instruction: chng.currentValue?.unsafeSubData.instruction ?? '',
            targetCallDuration: chng.currentValue?.unsafeSubData.targetCallDuration ?? 30,
            salesCycle: chng.currentValue?.unsafeSubData.salesCycleId ?? '',
            resume: chng.currentValue?.unsafeSubData.resumeId ?? '',
            product: chng.currentValue?.unsafeSubData.productId ?? '',
            goal: chng.currentValue?.unsafeSubData.goalId ?? '',
            methodology: chng.currentValue?.unsafeSubData.methodologyId ?? '',
          });
        }
      }
    }
  }

  onIsVisibleChange(isVisible: boolean) {
    this.isVisibleChange.emit(isVisible);
  }

  onDeleteClick() {
    if (this.sessionTemplate) {
      this.sessionTemplateService.delete$([this.sessionTemplate]).pipe(take(1)).subscribe();
    }

    this.isVisible = false;
    this.isVisibleChange.emit(this.isVisible);
  }

  onCancelClick() {
    this.isVisible = false;
    this.isVisibleChange.emit(this.isVisible);
  }

  onEditSoftSkillsObjectionClick() {
    this.isEmotionalObjection.set(true);
    this.isEditObjectionListVisible = true;
  }

  onEditFactObjectionClick() {
    this.isEmotionalObjection.set(false);
    this.isEditObjectionListVisible = true;
  }

  submitForm() {
    const title = this.validateForm.value.title ?? 'Default Scenario';
    const instruction = this.validateForm.value.instruction ?? '';
    const productId = this.validateForm.value.product ?? '';
    const goalId = this.validateForm.value.goal;
    const salesCycleId = this.validateForm.value.salesCycle;
    const methodologyId = this.validateForm.value.methodology;
    const resumeId = this.validateForm.value.resume;
    const targetCallDuration = this.validateForm.value.targetCallDuration ?? 30;

    if (this.isEdit && this.sessionTemplate) {
      this.sessionTemplate.title = title;
      this.sessionTemplate.unsafeSubData!.instruction = instruction;
      this.sessionTemplate.unsafeSubData!.productId = productId;
      this.sessionTemplate.unsafeSubData!.language = 'en';
      this.sessionTemplate.unsafeSubData!.goalId = goalId;
      this.sessionTemplate.unsafeSubData!.salesCycleId = salesCycleId;
      this.sessionTemplate.unsafeSubData!.methodologyId = methodologyId;
      this.sessionTemplate.unsafeSubData!.resumeId = resumeId;
      this.sessionTemplate.unsafeSubData!.targetCallDuration = targetCallDuration;

      this.sessionTemplateService
        .update$(this.sessionTemplate)
        .pipe(take(1))
        .subscribe(() => {
          this.isVisible = false;
          this.isVisibleChange.emit(this.isVisible);
        });
    } else {
      const creativity = this.creativityOptions[this.validateForm.value.creativity!];
      const performance = this.performanceOptions[this.validateForm.value.performance!];

      const additionalRubrics = this.validateForm.value.additionalRubrics!;

      const unsafeSubData: SessionTemplateUnSafeSubData = {
        instruction,
        goalId,
        methodologyId,
        salesCycleId,
        resumeId,
        productId,
        language: 'en',
        creativity,
        performance,
        targetCallDuration,
        additionalRubrics: additionalRubrics ? [additionalRubrics] : undefined,
      };

      this.sessionTemplateService
        .add$(title, unsafeSubData)
        .pipe(take(1))
        .subscribe(() => {
          this.isVisible = false;
          this.isVisibleChange.emit(this.isVisible);
        });
    }
  }
}
